import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  withStyles
} from '@material-ui/core/styles';
import {
  ClearRounded,
  CreateNewFolderRounded,
  DeleteRounded,
  FolderOutlined,
  LooksOneRounded,
  LooksTwoRounded,
  PeopleAltOutlined
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../app/store';
import { KeyboardDatePicker } from '../../components/CustomDatePicker';
// import HelpTooltip from '../../components/HelpTooltip'; // Commented out - not needed
import HierarchySelect from '../../components/HierarchySelect';
import { stringAvatar } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { AccessControl } from '../MasterData/AccessControl/accessControlSlice';
// import { ApprovalPeriod } from '../ProjectApprovalPeriod/projectApprovalPeriodSlice'; // Commented out - not needed
import { PlugAndPlayUser } from '../../api/users';
import { User } from '../UserProfile/userProfileSlice';
import {
  getBusinessUnitGroupsAsync,
  getBusinessUnitsAsync,
  getProjectStatusesAsync
} from './plugAndPlayMasterDataSlice';
import { getUsersAsync } from './plugAndPlayUsersSlice';
import { createPlugAndPlayProjectAsync } from './projectSlice';

enum StepperLabel {
  PROJECT = 0,
  MEMBER_ACCESS_CONTROL = 1,
  APPROVAL_PERIODS = 2,
  COMPLETED = 3
}

interface StepWorkflow {
  label: string;
  icon: JSX.Element;
}

interface Option {
  value: string;
  label: string;
}

interface ProjectFormData {
  projectNumber: string;
  projectName: string;
  plantName: string;
  customerCompany: string;
  businessUnitName: Option | null;
  businessUnitGroupName: Option | null;
  projectStatus: Option | null;
  productName: string;
  ndaExpireDate: Date | null;
}

export default function PlugAndPlayProjectCreate() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Get data from Redux (same as ProjectCreate.tsx)
  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  // const { data: workflowSteps } = useSelector(
  //   (state: RootState) => state.workflowSteps
  // ); // Commented out - not needed

  // Get Plug and Play data from Redux
  const { data: plugAndPlayUsers } = useSelector(
    (state: RootState) => state.plugAndPlayUsers
  );

  // Use PlugAndPlayUser directly
  const users: PlugAndPlayUser[] = plugAndPlayUsers;
  const {
    businessUnitGroups: { data: businessUnitGroupsData },
    businessUnits: { data: businessUnitsData },
    projectStatuses: { data: projectStatusesData }
  } = useSelector((state: RootState) => state.plugAndPlayMasterData);

  // Transform API data to options format
  const businessUnitOptions = businessUnitsData.map((unit) => ({
    value: unit.name,
    label: unit.name
  }));

  const businessUnitGroupOptions = businessUnitGroupsData.map((group) => ({
    value: group.name,
    label: group.name
  }));

  const projectStatusOptions = projectStatusesData.map((status) => ({
    value: status.name,
    label: status.name
  }));

  // Fallback options commented out (not used)
  // const fallbackBusinessUnitOptions = businessUnitOptions.length > 0 ? businessUnitOptions : [
  //   { value: 'engineering', label: 'Engineering' },
  //   { value: 'construction', label: 'Construction' },
  //   { value: 'procurement', label: 'Procurement' },
  //   { value: 'quality', label: 'Quality Assurance' },
  // ];

  const steps: StepWorkflow[] = [
    { label: t('projectCreate.stepper.createNewProject'), icon: <FolderOutlined fontSize="small" /> },
    {
      label: t('projectCreate.stepper.assignMemberAndAccessLevels'),
      icon: <PeopleAltOutlined fontSize="small" />
    }
    // { label: 'Set approval periods', icon: <TimerOutlined fontSize="small" /> }
  ];
  const [activeStep, setActiveStep] = useState<number>(0);

  // Form data state
  const [formData, setFormData] = useState<ProjectFormData>({
    projectNumber: '',
    projectName: '',
    plantName: '',
    customerCompany: '',
    businessUnitName: null,
    businessUnitGroupName: null,
    projectStatus: null,
    productName: '',
    ndaExpireDate: null
  });
  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});

  // Member assignment state
  const [selectedMembers, setSelectedMembers] = useState<User[]>([]);
  const [selectedAccessControls, setSelectedAccessControls] = useState<
    AccessControl[]
  >([]);
  const [memberLists, setMemberLists] = useState<
    { user: User; accessControls: AccessControl[] }[]
  >([]);

  // Approval periods state - Backend decision pending (commented out)
  // const defaultApprovalPeriod = workflowSteps.map((workflowStep) => {
  //   return { workflowStepId: workflowStep.id, value: null };
  // });
  // const [approvalPeriods, setApprovalPeriods] = useState<ApprovalPeriod[]>(
  //   defaultApprovalPeriod
  // );
  const [loading, setLoading] = useState<boolean>(false);

  // const showAlertRequestAccess = users.some(
  //   ({ roles }) => roles && roles.length === 0
  // );

  useEffect(() => {
    // Load Plug and Play APIs
    dispatch(getUsersAsync());
    dispatch(getBusinessUnitGroupsAsync());
    dispatch(getBusinessUnitsAsync());
    dispatch(getProjectStatusesAsync());
  }, [dispatch]);

  // Form handlers
  const handleChange =
    (field: keyof ProjectFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value
      });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors({
          ...errors,
          [field]: ''
        });
      }
    };

  const handleDateChange = (date: Date | null) => {
    setFormData({
      ...formData,
      ndaExpireDate: date
    });
  };

  const validateForm = () => {
    const newErrors: Partial<ProjectFormData> = {};

    if (!formData.projectNumber) {
      newErrors.projectNumber = t('projectCreate.validationErrors.projectNumberRequired') as string;
    }
    if (!formData.projectName) {
      newErrors.projectName = t('validation.projectNameRequired') as string;
    }
    if (!formData.customerCompany) {
      newErrors.customerCompany = t('validation.customerCompanyRequired') as string;
    }
    if (!formData.projectStatus) {
      newErrors.projectStatus = t('projectCreate.validationErrors.projectStatusRequired') as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Member assignment handlers
  function onChangeAccessControl(accessControls: AccessControl[]): void {
    setSelectedAccessControls(accessControls);
  }

  function onChangeAccessControlByUserId(
    userId: number
  ): (accessControls: AccessControl[]) => void {
    return function (accessControls) {
      const memberListIndex = memberLists.findIndex(
        (memberList) => memberList.user.id === userId
      );
      const newMemberList = {
        ...memberLists[memberListIndex],
        accessControls
      };
      const newMemberLists = [
        ...memberLists.slice(0, memberListIndex),
        newMemberList,
        ...memberLists.slice(memberListIndex + 1)
      ];
      setMemberLists(newMemberLists);
    };
  }

  // Approval periods handlers - Backend decision pending (commented out)
  // function onChangeApprovalPeriods(
  //   event: React.ChangeEvent<HTMLInputElement>
  // ): void {
  //   const { name, value } = event.target;
  //   const workflowStepId = Number.parseInt(name, 10);
  //   const newValue = Number.parseInt(value, 10);
  //   const newApprovalPeriods = approvalPeriods.filter(
  //     (approvalPeriod) => approvalPeriod.workflowStepId !== workflowStepId
  //   );
  //   newApprovalPeriods.push({
  //     workflowStepId,
  //     value: value === '' || newValue <= 0 ? null : newValue
  //   });
  //   setApprovalPeriods(newApprovalPeriods);
  // }

  // Form submission
  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    if (activeStep === StepperLabel.PROJECT) {
      if (validateForm()) {
        setActiveStep(StepperLabel.MEMBER_ACCESS_CONTROL);
      }
      return;
    }

    if (activeStep === StepperLabel.MEMBER_ACCESS_CONTROL) {
      if (memberLists.length > 0) {
        // Skip approval periods step and directly create project
        try {
          setLoading(true);

          // Prepare data for Plug and Play API - match Postman collection format
          const plugAndPlayProjectData = {
            businessUnitGroupName: formData.businessUnitGroupName?.label || '',
            businessUnitName: formData.businessUnitName?.label || '',
            customerCompanyName: formData.customerCompany,
            plantName: formData.plantName || '',
            productName: formData.productName || '',
            projectNo: formData.projectNumber,
            name: formData.projectName,
            projectStatus: formData.projectStatus?.label || '',
            ndaExpireDate: formData.ndaExpireDate
              ? formData.ndaExpireDate.toISOString()
              : undefined,
            projectAccessControlUsers: memberLists.map((member) => ({
              userId: member.user.id,
              accessControlIds: member.accessControls.map(
                (accessControl) => accessControl.id
              )
            }))
            //approvalPeriods: [] // Empty array - Required by TypeScript but Backend decision pending
          };

          console.log(
            'Creating Plug and Play project:',
            plugAndPlayProjectData
          );

          // Call actual Plug and Play API
          await dispatch(
            createPlugAndPlayProjectAsync(plugAndPlayProjectData)
          ).unwrap();

          dispatch(
            loadingSlice.actions.success({
              message: t('messages.success.projectCreated')
            })
          );
          setActiveStep(StepperLabel.COMPLETED);
          setTimeout(() => {
            history.push('/projects');
          }, 1000);
        } catch (error) {
          console.error('Error creating Plug and Play project:', error);
          dispatch(
            loadingSlice.actions.error({
              message: t('messages.error.failedToCreateProject')
            })
          );
          setLoading(false);
        }
      }
      return;
    }

    // Approval periods step is commented out
    // if (activeStep === StepperLabel.APPROVAL_PERIODS) { ... }
  }

  const handleCancel = () => {
    history.goBack();
  };

  const isProjectFormValid = () => {
    return (
      formData.projectNumber &&
      formData.projectName &&
      formData.customerCompany &&
      formData.projectStatus
    );
  };

  return (
    <div className={classes.root}>
      <Breadcrumb name="setUpNewProject" />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectCreate.createProject')} - Plug & Play
      </Typography>
      <Paper className={classes.paper}>
        <Stepper
          className={classes.stepper}
          alternativeLabel
          activeStep={activeStep}
          connector={<Connector />}
        >
          {steps.map(({ label, icon }) => (
            <Step key={label}>
              <StepLabel StepIconComponent={(props) => StepIcon(props, icon)}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <form onSubmit={onSubmit}>
        {/* Step 1: Create New Project Form */}
        <Collapse in={activeStep >= StepperLabel.PROJECT}>
          <Paper className={classes.paper}>
            <div className={classes.stepText}>
              <LooksOneRounded color="primary" />
              <Typography variant="subtitle2">{t('projectCreate.stepper.createNewProject')}</Typography>
            </div>

            <Box className={classes.sectionHeader}>
              <Typography variant="h6" className={classes.sectionTitle}>
                {t('projectCreate.projectInformation')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('projectCreate.projectInformationSubtitle')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t('projectCreate.projectNumber')}
                  placeholder={t('projectCreate.placeholders.projectNumber') as string}
                  value={formData.projectNumber}
                  onChange={handleChange('projectNumber')}
                  error={!!errors.projectNumber}
                  helperText={errors.projectNumber}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t('projectCreate.projectName')}
                  placeholder={t('projectCreate.placeholders.enterProjectName') as string}
                  value={formData.projectName}
                  onChange={handleChange('projectName')}
                  error={!!errors.projectName}
                  helperText={errors.projectName}
                  required
                />
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t('projectCreate.plantName')}
                  placeholder={t('projectCreate.placeholders.enterPlantName') as string}
                  value={formData.plantName}
                  onChange={handleChange('plantName')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t('projectCreate.customerCompany')}
                  placeholder={t('projectCreate.placeholders.enterCustomerCompanyName') as string}
                  value={formData.customerCompany}
                  onChange={handleChange('customerCompany')}
                  error={!!errors.customerCompany}
                  helperText={errors.customerCompany}
                  required
                />
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="business-unit-name"
                  options={businessUnitOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionSelected={(option, value) =>
                    option?.value === value?.value
                  }
                  value={formData.businessUnitName}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      businessUnitName: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectCreate.businessUnit')}
                      placeholder={t('projectCreate.placeholders.selectBusinessUnit') as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="business-unit-group-name"
                  options={businessUnitGroupOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionSelected={(option, value) =>
                    option?.value === value?.value
                  }
                  value={formData.businessUnitGroupName}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      businessUnitGroupName: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectCreate.businessUnitGroup')}
                      placeholder={t('projectCreate.placeholders.selectBusinessUnitGroup') as string}
                    />
                  )}
                />
              </Grid>

              {/* Row 4 */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="project-status"
                  options={projectStatusOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionSelected={(option, value) =>
                    option?.value === value?.value
                  }
                  value={formData.projectStatus}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      projectStatus: newValue
                    });
                    // Clear error when user selects
                    if (errors.projectStatus) {
                      setErrors({
                        ...errors,
                        projectStatus: undefined
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label={t('projectCreate.projectStatus')}
                      placeholder={t('projectCreate.placeholders.selectProjectStatus') as string}
                      error={!!errors.projectStatus}
                      helperText={errors.projectStatus}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t('projectCreate.product')}
                  placeholder={t('projectCreate.placeholders.enterProductName') as string}
                  value={formData.productName}
                  onChange={handleChange('productName')}
                />
              </Grid>

              {/* Row 5 - NDA Expire Date */}
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  fullWidth
                  inputVariant="outlined"
                  size="small"
                  label="NDA Expire Date"
                  placeholder="Pick a date"
                  value={formData.ndaExpireDate}
                  onChange={handleDateChange}
                  autoOk
                  variant="inline"
                  InputAdornmentProps={{
                    position: 'end'
                  }}
                  className={classes.datePicker}
                />
              </Grid>

              {/* Helper text */}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.helperText}
                >
                  Select the expiration date for the Non-Disclosure Agreement
                </Typography>
              </Grid>
            </Grid>

            {/* Action buttons for step 1 */}
            <div className={classes.collapseButton}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!isProjectFormValid()}
              >
                {t('projectCreate.buttons.add')}
              </Button>
              <Button
                className={classes.marginLeft}
                fullWidth
                onClick={handleCancel}
              >
                {t('projectCreate.buttons.cancel')}
              </Button>
            </div>
          </Paper>
        </Collapse>

        {/* Step 2: Assign Members and Access Levels */}
        <Collapse in={activeStep >= StepperLabel.MEMBER_ACCESS_CONTROL}>
          <Paper className={classes.paper}>
            <div className={classes.stepText}>
              <LooksTwoRounded color="primary" />
              <Typography variant="subtitle2">
                {t('projectCreate.stepLabels.assignMembersAndAccessLevels')}
              </Typography>
            </div>
            {/* {showAlertRequestAccess && (
              <Alert
                className={classes.alert}
                severity="info"
                icon={false}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.open(
                        `${process.env.REACT_APP_REQUEST_FOR_BACKSPACE_WEB_URL}?systemName=doc`,
                        '_blank'
                      );
                    }}
                  >
                    Request Access
                  </Button>
                }
              >
                You can assign only member who can access <b>Space DOCUMENT</b>
              </Alert>
            )} */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  id="add-member"
                  size="small"
                  multiple
                  disableCloseOnSelect
                  options={users.filter(
                    (user) =>
                      !memberLists.some(
                        (memberList) => memberList.user.id === user.id
                      )
                  )}
                  getOptionLabel={(option) =>
                    `${option?.firstName} ${option?.lastName}`
                  }
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        key={index}
                        label={`${option?.firstName} ${option?.lastName}`}
                        {...getTagProps({ index })}
                        variant="outlined"
                        size="small"
                        deleteIcon={<ClearRounded />}
                      />
                    ))
                  }
                  value={selectedMembers}
                  getOptionSelected={(option, value) => option.id === value?.id}
                  // getOptionDisabled={(option) => option.roles?.length === 0}
                  onChange={(_, newValue) => {
                    setSelectedMembers(newValue);
                    if (newValue.length === 0) {
                      setSelectedAccessControls([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        selectedMembers.length === 0 ? (t('projectCreate.placeholders.selectMember') as string) : ''
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <HierarchySelect<AccessControl>
                  placeholder={t('projectCreate.placeholders.accessLevel') as string}
                  options={accessControls}
                  value={selectedAccessControls}
                  onChange={onChangeAccessControl}
                  required={selectedMembers.length > 0}
                />
              </Grid>
            </Grid>
            <Collapse in={selectedMembers.length > 0}>
              <div className={classes.collapseButton}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={selectedAccessControls.length === 0}
                  onClick={() => {
                    const members = selectedMembers.map((member) => {
                      return {
                        user: member,
                        accessControls: selectedAccessControls
                      };
                    });
                    const newMemberLists = memberLists.filter(
                      (memberList) =>
                        !members.some(
                          (member) => member.user.id === memberList.user.id
                        )
                    );
                    setMemberLists([...newMemberLists, ...members]);
                    setSelectedMembers([]);
                    setSelectedAccessControls([]);
                    // setActiveStep(StepperLabel.APPROVAL_PERIODS); // Commented out - skip to project creation
                  }}
                >
                  {t('projectCreate.buttons.add')}
                </Button>
                <Button
                  className={classes.marginLeft}
                  fullWidth
                  onClick={() => {
                    setSelectedMembers([]);
                    setSelectedAccessControls([]);
                  }}
                >
                  {t('projectCreate.buttons.cancel')}
                </Button>
              </div>
            </Collapse>
            <Grid
              className={clsx(classes.marginTop, {
                [classes.clearMarginTop]: memberLists.length === 0
              })}
              container
              spacing={memberLists.length === 0 ? 0 : 1}
            >
              {memberLists
                .sort((a, b) => {
                  const nameA = `${a.user.firstName.toUpperCase()}${a.user.lastName.toUpperCase()}`; // ignore upper and lowercase
                  const nameB = `${b.user.firstName.toUpperCase()}${b.user.lastName.toUpperCase()}`; // ignore upper and lowercase
                  return nameA.localeCompare(nameB);
                })
                .map((memberList) => {
                  return (
                    <Fragment key={memberList.user.id}>
                      <Grid
                        className={classes.flexAlignCenter}
                        item
                        xs={12}
                        sm={4}
                      >
                        <Avatar
                          className={classes.avatar}
                          {...stringAvatar(
                            `${memberList.user.firstName} ${memberList.user.lastName}`
                          )}
                        />
                        <Typography
                          className={classes.lineHeight120}
                        >{`${memberList.user.firstName} ${memberList.user.lastName}`}</Typography>
                      </Grid>
                      <Grid
                        className={classes.flexAlignCenter}
                        item
                        xs={12}
                        sm={8}
                      >
                        <HierarchySelect<AccessControl>
                          placeholder={t('projectCreate.placeholders.accessLevel') as string}
                          options={accessControls}
                          value={memberList.accessControls}
                          onChange={onChangeAccessControlByUserId(
                            memberList.user.id
                          )}
                          required
                        />
                        <Tooltip title={t('common.remove') as string} arrow>
                          <IconButton
                            className={classes.marginLeft}
                            size="small"
                            onClick={() => {
                              const newMemberLists = memberLists.filter(
                                (member) =>
                                  memberList.user.id !== member.user.id
                              );
                              setMemberLists(newMemberLists);
                            }}
                          >
                            <DeleteRounded />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Fragment>
                  );
                })}
            </Grid>
          </Paper>

          {/* Add project button when member assignment is completed */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading || memberLists.length === 0}
            startIcon={<CreateNewFolderRounded />}
          >
            {t('projectCreate.buttons.addThisProject')}
          </Button>
        </Collapse>

        {/* Step 3: Set Approval Periods - Backend decision pending (commented out) */}
        {/* <Collapse in={activeStep >= StepperLabel.APPROVAL_PERIODS}>
          <Paper className={classes.paper}>
            <div className={classes.stepText}>
              <Looks3Rounded color="primary" />
              <Typography variant="subtitle2">{t('projectCreate.stepLabels.setApprovalPeriods')}</Typography>
              <HelpTooltip>
                {t('projectCreate.helpText.approvalPeriodsHelp')}
              </HelpTooltip>
            </div>
            <Collapse in={selectedMembers.length > 0}>
              <Alert className={classes.marginBottom} severity="warning">
                {t('projectCreate.helpText.dontForgetMembersAlert')}
              </Alert>
            </Collapse>
            <Grid container spacing={0} justify="flex-start">
              <Grid item xs={12} md={6}>
                {workflowSteps.map((workflowStep) => {
                  const approvalPeriod = approvalPeriods.find(
                    (approvalPeriod) =>
                      approvalPeriod.workflowStepId === workflowStep.id
                  );
                  return (
                    <div
                      className={clsx(
                        classes.marginBottom,
                        classes.positionText
                      )}
                      key={workflowStep.name}
                    >
                      <Typography className={classes.flexGrow}>
                        {workflowStep.name}
                      </Typography>
                      <TextField
                        className={classes.numberField}
                        name={`${workflowStep.id}`}
                        type="number"
                        variant="outlined"
                        size="small"
                        value={
                          approvalPeriod?.value ? approvalPeriod.value : ''
                        }
                        onChange={onChangeApprovalPeriods}
                      />
                      <Typography>{t('projectCreate.other.days')}</Typography>
                    </div>
                  );
                })}
              </Grid>
            </Grid>

          </Paper>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={
              loading ||
              approvalPeriods.every(
                (approvalPeriod) => approvalPeriod.value === null
              )
            }
            startIcon={<CreateNewFolderRounded />}
          >
            {t('projectCreate.buttons.addThisProject')}
          </Button>
        </Collapse> */}
      </form>
    </div>
  );
}

function StepIcon(props: StepIconProps, icon: JSX.Element) {
  const classes = useStepIconStyles();
  const { active, completed } = props;

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.success]: completed
      })}
    >
      {icon}
    </div>
  );
}

const Connector = withStyles((theme: Theme) =>
  createStyles({
    alternativeLabel: {
      top: 16
    },
    active: {
      '& $line': {
        backgroundImage: `linear-gradient( 90deg,${theme.palette.success.main} 0%,${theme.palette.primary.main} 100%)`
      }
    },
    completed: {
      '& $line': {
        backgroundColor: theme.palette.success.main
      }
    },
    line: {
      height: 2,
      border: 0,
      backgroundColor: theme.palette.divider,
      borderRadius: 1
    }
  })
)(StepConnector);

const useStepIconStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.divider,
      zIndex: 1,
      color: '#fff',
      width: 32,
      height: 32,
      display: 'flex',
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center'
    },
    active: {
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, .25)'
    },
    success: {
      backgroundColor: theme.palette.success.main
    }
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      borderRadius: 16,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    stepper: {
      padding: theme.spacing(1, 0, 0, 0)
    },
    stepText: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(0.5, 0, 1.5, 0),
      '& > :nth-child(1)': {
        marginRight: theme.spacing(1)
      }
    },
    sectionHeader: {
      marginBottom: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5)
    },
    positionText: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 2)
    },
    numberField: {
      width: 80,
      paddingRight: 16
    },
    alert: {
      marginBottom: theme.spacing(1)
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0
      }
    },
    clearMarginTop: {
      marginTop: 0
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    fullWidth: {
      width: '100%'
    },
    checkIcon: {
      margin: theme.spacing(-2)
    },
    collapseButton: {
      display: 'flex',
      paddingTop: theme.spacing(1)
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: theme.spacing(1)
    },
    lineHeight120: {
      lineHeight: '120%'
    },
    datePicker: {
      '& .MuiInputBase-root': {
        paddingRight: 0
      }
    },
    helperText: {
      display: 'block',
      marginTop: theme.spacing(-2)
    }
  })
);
